<script setup lang="ts">
import { InquiryType } from "@mmh/types/constants"
import type { HTMLAttributes } from "vue"
import { useRoute, useRouter } from "vue-router"
import { InquiryDialogV2 } from "~/components/inquiry"
import { ThemeButton, ThemeImg } from "~/components/theme"
import { Separator } from "~/components/ui/separator"
import { useLocation } from "~/composables/locations"
import { scrollToAndFocus } from "~/composables/scroll"
import { cn } from "~/lib/utils"

const { class: cl } = defineProps<{
    class?: HTMLAttributes["class"]
}>()

const { location, menuPath } = useLocation()
const router = useRouter()
const route = useRoute()

async function handleScrollToMenu(event: Event) {
    event.preventDefault()
    
    await router.push(menuPath("large-party"))
    
    scrollToAndFocus("menus")
}
</script>

<template>
    <div
        v-if="location"
        id="large-party-reservations"
        tabindex="-1"
        :class="cn('space-y-10 md:space-y-12 px-4 sm:px-6', cl)"
    >
        <h2 class="text-2xl sm:text-3xl lg:text-4xl font-headline text-center text-balance max-w-full overflow-hidden">
            {{ location?.pageContent?.largePartyReservations?.title || '' }}
        </h2>
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8">
            <ThemeImg
                :src="location?.pageContent?.largePartyReservations?.image || ''"
                :alt="location?.pageContent?.largePartyReservations?.imageAlt || ''"
                class="w-full aspect-[4/3] lg:aspect-auto lg:h-full object-cover lg:max-h-[525px]"
            />
            <div class="flex flex-col gap-6 justify-around lg:p-8 bg-foreground-faded border-l-8 border-background/50 min-w-0 py-6">
                <div class="flex flex-col justify-center items-center space-y-6">
                    <header class="space-y-4 text-center text-balance">
                        <h3 class="text-xl font-headline">
                            Get Together For a Memorable Experience
                        </h3>
                        <Separator class="mx-auto w-10 bg-foreground/20" />
                    </header>
                    <div class="space-y-4 max-w-lg mx-auto text-center">
                        <p
                            class="text-balance break-words max-w-full"
                            v-html="location?.pageContent?.largePartyReservations?.content || ''"
                        />
                    </div>
                </div>
                <div class="flex flex-col items-stretch sm:items-center justify-center gap-4 mx-auto">
                    <ThemeButton 
                        size="lg"
                        variant="outline"
                        class="border-foreground/20 w-full sm:w-auto text-balance"
                        tabindex="0"
                        aria-label="View Prix Fixe Menus"
                        @click="handleScrollToMenu"
                    >
                        View Prix Fixe Menus
                    </ThemeButton>
                    <InquiryDialogV2
                        :type="InquiryType.LargeParty"
                        :location="location"
                        :to="{ path: route.path, query: { modal: 'large-party-inquiry-form' } }"
                        trigger-type="large_party_section"
                    >
                        <ThemeButton 
                            size="lg"
                            class="w-full sm:w-auto !whitespace-normal break-words text-center"
                            tabindex="0"
                        >
                            Inquire Now
                        </ThemeButton>
                    </InquiryDialogV2>
                </div>
            </div>
        </div>
    </div>
</template>